import { useImperativeHandle, useState } from 'react';

import { DatasheetViewerBoundingBoxRenderer } from '#components/datasheet-viewer/datasheet-viewer-bounding-box-renderer.js';

import type { Ref } from 'react';
import type { DatasheetPopoverBoundingBox } from './datasheet-popover-store.js';

/** Moves a {@link DatasheetPdfFrame} and reports where it is. */
export interface DatasheetPdfFrameHandle {
	/** The 1-based page the frame was last sent to, or opened on. */
	pageNumber: number;
	/** Shows a page, scrolling back to it even when it is the page already shown. */
	goToPage: (pageNumber: number) => void;
}

/** Props for {@link DatasheetPdfFrame}. */
export interface DatasheetPdfFrameProps {
	/** Address of the PDF to show. */
	pdfUrl: string;
	/** Bounding boxes drawn over the PDF; empty for none. */
	boundingBoxes: DatasheetPopoverBoundingBox[];
	/** Accessible title of the frame. */
	title: string;
	/** The 1-based page the frame opens on. */
	initialPageNumber: number;
	/** Receives the handle that moves the frame. */
	ref?: Ref<DatasheetPdfFrameHandle>;
}

/**
 * A datasheet PDF in the browser's viewer, with bounding boxes drawn over it. It opens on its initial page and moves
 * only when told to through its handle; a change of bounding boxes keeps the page it was last sent to.
 */
export function DatasheetPdfFrame({ pdfUrl, boundingBoxes, title, initialPageNumber, ref }: DatasheetPdfFrameProps) {
	const [pageNumber, setPageNumber] = useState(initialPageNumber);
	// The browser's PDF viewer reads the page from the address only on load, so each move loads a new frame.
	const [frameKey, setFrameKey] = useState(0);

	useImperativeHandle(
		ref,
		() => ({
			pageNumber,
			goToPage: (nextPageNumber: number) => {
				setPageNumber(nextPageNumber);
				setFrameKey((key) => key + 1);
			},
		}),
		[pageNumber],
	);

	const renderFrame = (url: string) => (
		<iframe key={frameKey} src={`${url}#page=${pageNumber}`} title={title} className='h-full w-full border-0' />
	);

	if (boundingBoxes.length === 0) {
		return renderFrame(pdfUrl);
	}
	return <DatasheetViewerBoundingBoxRenderer blobUrl={pdfUrl} boundingBoxes={boundingBoxes} render={renderFrame} />;
}
